import { select, take, put, all, takeEvery } from 'redux-saga/effects'
import { flatten } from 'ramda'
import {
  setOutputField,
  ADD_OUTPUT_NODE,
  UPDATE_OUTPUT_QUERY,
  updateOutputNode,
  UPDATE_OUTPUT_NODE,
} from 'actions/feature/outputs'
import {
  ADD_SUGGESTED_OUTPUT,
  addSuggestedOutputNode,
  deleteSingleCondition,
  resetConditionShowHelp,
} from 'actions/feature/conditions'
import { getSignal } from 'selectors/feature/inputs'
import { AH, SERVICE, PIN, BOOLEAN, NATIVE_INPUT } from 'utils/constants'
import strings from 'utils/strings'
import {
  getOutputQueryType,
  getRelativeOutput,
  getOutputQuery,
} from 'selectors/feature/outputs'
import { getFeatureModuleName } from 'selectors/feature/featureMetadata'

import {
  isTimer,
  isLogicSet,
  isAnalogInput,
  isDigitalInput,
  isBooleanOutput,
  isBooleanNativeOutput,
  isNativeInput,
  isNonBooleanNativeOutput,
  isNativeOutput,
} from 'utils/types/features'

import { OPERATORS, EVAL_VALUES } from 'components/utils'
import { DEFAULT_POWER_NET_LEVEL } from 'constants/featureOutputs'
import { getFunctionOutputs } from 'selectors/feature/functions'
import { switchInputType } from 'actions/feature/inputs'
import { getInOr } from 'utils/immutable'
import { Map } from 'immutable'
import { getCondition } from 'selectors/feature/conditions'

export function* watchAddOutput() {
  while (true) {
    const { tmpId, fnId } = yield take(ADD_OUTPUT_NODE)
    const outputQueryType = yield select(getOutputQueryType, { fnId })

    const featureModuleName = yield select(getFeatureModuleName)
    const defaultToNative = featureModuleName === !isTimer(outputQueryType)
    const outputType = defaultToNative ? SERVICE : PIN

    if (outputType === PIN) {
      yield put(
        updateOutputNode(tmpId, {
          polarity: AH,
          current: 5,
          type: outputType,
          powerNetLevel: DEFAULT_POWER_NET_LEVEL,
        })
      )
    } else {
      yield put(setOutputField('type', outputType, tmpId))
    }
  }
}

export function* watchAddOutputQuery() {
  yield takeEvery(UPDATE_OUTPUT_QUERY, updateOutputQuery)
}

export function* watchUpdateOutputNode() {
  yield takeEvery(UPDATE_OUTPUT_NODE, checkOutputNode)
}

function* updateOutputQuery({ fnId = null } = {}) {
  const outputQuery = yield select(getOutputQuery, { fnId }) || {}
  const { uri, queryType, subType, signal: signalName } = outputQuery
  const outputQueryType = yield select(getOutputQueryType, { fnId })
  const outputs = yield select(getFunctionOutputs, { id: fnId })
  const signal = yield select(getSignal, {
    signal: signalName,
  })

  const allCalls = outputs.map(({ id, ...output }) => {
    const newVals = {
      operator: null,
      value: null,
    }

    if (isTimer(outputQueryType)) {
      newVals.operator = OPERATORS.EQUAL
      newVals.value = 0
    } else if (isLogicSet(outputQueryType)) {
      newVals.operator = OPERATORS.EQUAL
      newVals.value = EVAL_VALUES.TRUE
    }

    // when boolean native output and non-boolean input
    // or non boolean native output and boolean input
    const hasConflictingInputSignal =
      (isBooleanNativeOutput(output) &&
        isNativeInput(subType) &&
        signal &&
        signal.translationType &&
        signal.translationType !== BOOLEAN) ||
      (isNonBooleanNativeOutput(output) &&
        isNativeInput(subType) &&
        signal &&
        signal.translationType &&
        signal.translationType === BOOLEAN)
    // console.log({ hasConflictingInputSignal })
    // conditional spreads check for a boolean output with non-boolean input, and switch the input type
    return [
      ...(hasConflictingInputSignal
        ? [put(switchInputType(uri, NATIVE_INPUT, {}, queryType))]
        : []),
      ...(isBooleanNativeOutput(output) && isAnalogInput(subType)
        ? [put(deleteSingleCondition(uri, fnId))]
        : []),
      ...(isNativeOutput(output.type) &&
      !isBooleanOutput(output) &&
      isDigitalInput(subType)
        ? [put(deleteSingleCondition(uri, fnId))]
        : []),
      put(setOutputField('value', newVals.value, id)),
      put(setOutputField('operator', newVals.operator, id)),
    ]
  })
  yield all(flatten(allCalls))
}

export function* watchAddHelperOutput() {
  while (true) {
    const { tmpId, relatedOutputUri, fnId, newIndex, uri } = yield take(
      ADD_SUGGESTED_OUTPUT
    )
    const outputId = relatedOutputUri.split('/')[2]
    const outputDetails = yield select(getRelativeOutput, { outputId })
    outputDetails.fnId = fnId
    outputDetails.mappedOutputId = outputDetails.id
    outputDetails.description = `${strings.output} ${newIndex}`
    outputDetails.id = tmpId

    yield put(addSuggestedOutputNode(outputDetails, fnId, tmpId))
    yield put(resetConditionShowHelp(uri))
  }
}

export function* checkOutputNode({ id } = {}) {
  const { fnId, ...output } = yield select((state) =>
    getInOr(Map({}), ['feature', 'currentFeature', 'outputs', id], state).toJS()
  )
  if (fnId) {
    const outputQuery = yield select(getOutputQuery, { fnId })
    if (outputQuery) {
      const condition = yield select(getCondition, {
        queryUri: outputQuery.uri,
      })
      const signal = yield select(getSignal, {
        signal: condition.signal,
      })
      const isBooleanSignal = signal.translationType === BOOLEAN
      const isNonBooleanSignal =
        signal.translationType && signal.translationType !== BOOLEAN
      const hasConflictingBits =
        (isBooleanNativeOutput(output) && isNonBooleanSignal) ||
        (isNonBooleanNativeOutput(output) && isBooleanSignal)
      if (hasConflictingBits) {
        yield put(
          switchInputType(
            outputQuery.uri,
            NATIVE_INPUT,
            {},
            outputQuery.queryType
          )
        )
      }
    }
  }
}
